import { useState } from 'react';
import Swal from 'sweetalert2';
import type { AdminLive, AdminLiveRecording } from '@/mocks/adminLive';
import { providerLabels, providerIcons } from '@/mocks/adminLive';

export type PostLiveAction = 'recording' | 'video' | 'post' | 'archive' | 'notify';

export interface PostLiveFlowResult {
  action: PostLiveAction;
  recording?: AdminLiveRecording;
  videoData?: {
    title: string;
    description: string;
    category: string;
    sourceType: 'upload' | 'youtube' | 'facebook';
    sourceUrl: string;
    duration: string;
    status: 'borrador' | 'publicado' | 'programado';
    isFeatured: boolean;
  };
  postData?: {
    title: string;
    text: string;
    type: 'texto' | 'imagen' | 'video';
    status: 'borrador' | 'publicado' | 'programado';
  };
  notifyContext?: {
    type: string;
    relatedModule: string;
    relatedItemId: number;
    relatedItemName: string;
    prefilledTitle: string;
    prefilledMessage: string;
  };
}

interface PostLiveFlowModalProps {
  open: boolean;
  live: AdminLive;
  recording: AdminLiveRecording | undefined;
  onClose: () => void;
  onAction: (result: PostLiveFlowResult) => void;
}

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

export default function PostLiveFlowModal({ open, live, recording, onClose, onAction }: PostLiveFlowModalProps) {
  const [step, setStep] = useState<'menu' | 'video_form' | 'post_form' | 'done'>('menu');
  const [videoTitle, setVideoTitle] = useState(live.title);
  const [videoDesc, setVideoDesc] = useState(live.description);
  const [videoCategory, setVideoCategory] = useState('eventos');
  const [videoDuration, setVideoDuration] = useState('1:00:00');
  const [videoStatus, setVideoStatus] = useState<'borrador' | 'publicado' | 'programado'>('borrador');
  const [videoFeatured, setVideoFeatured] = useState(false);
  const [postTitle, setPostTitle] = useState(`Reviví: ${live.title}`);
  const [postText, setPostText] = useState(live.description);
  const [postStatus, setPostStatus] = useState<'borrador' | 'publicado' | 'programado'>('publicado');

  if (!open) return null;

  const hasRecording = !!recording;
  const hasVideo = !!live.generatedVideoId;
  const hasPost = !!live.generatedPostId;

  const handleCreateRecording = () => {
    const mockRecording: AdminLiveRecording = {
      id: Date.now(),
      liveId: live.id,
      title: live.title,
      description: live.description,
      hostDisplayName: live.host,
      date: live.date,
      duration: '1:00:00',
      thumbnailUrl: live.imageUrl,
      platform: providerLabels[live.provider],
      audiencePeak: Math.floor(200 + Math.random() * 2000),
      audienceAvg: Math.floor(100 + Math.random() * 1000),
      totalViews: Math.floor(500 + Math.random() * 5000),
      status: 'available',
      createdAt: new Date().toISOString(),
    };

    sharedSwal.fire({
      title: 'Grabación creada',
      html: `<p class="text-sm text-foreground-500">Se creó un registro de grabación para <strong>&ldquo;${escapeHtml(live.title)}&rdquo;</strong>.</p>
      <div class="mt-3 bg-background-100 rounded-xl p-3 text-left space-y-1.5">
        <p class="text-xs text-foreground-600"><i class="ri-calendar-line mr-1"></i> ${new Date(live.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        <p class="text-xs text-foreground-600"><i class="ri-time-line mr-1"></i> Duración estimada: 1:00:00</p>
        <p class="text-xs text-foreground-600"><i class="ri-group-line mr-1"></i> Pico de audiencia: ${mockRecording.audiencePeak} oyentes</p>
        <p class="text-xs text-foreground-400 mt-1">El estado del live cambiará a "Grabación disponible".</p>
      </div>`,
      icon: 'success',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#10b981',
    }).then(() => {
      onAction({ action: 'recording', recording: mockRecording });
    });
  };

  const handlePublishAsVideo = () => {
    setStep('video_form');
  };

  const handleConfirmVideo = () => {
    sharedSwal.fire({
      title: '¿Crear video?',
      html: `<p class="text-sm text-foreground-500">Se creará un video a partir del live <strong>&ldquo;${escapeHtml(videoTitle)}&rdquo;</strong> y se publicará en estado <strong>${videoStatus}</strong>.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Crear video',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#9ca3af',
    }).then((result) => {
      if (result.isConfirmed) {
        onAction({
          action: 'video',
          videoData: {
            title: videoTitle,
            description: videoDesc,
            category: videoCategory,
            sourceType: 'upload',
            sourceUrl: '',
            duration: videoDuration,
            status: videoStatus,
            isFeatured: videoFeatured,
          },
        });
      }
    });
  };

  const handleCreatePost = () => {
    setPostTitle(`Reviví: ${live.title}`);
    setPostText(live.description);
    setStep('post_form');
  };

  const handleConfirmPost = () => {
    sharedSwal.fire({
      title: '¿Crear publicación?',
      html: `<p class="text-sm text-foreground-500">Se creará una publicación a partir del live <strong>&ldquo;${escapeHtml(postTitle)}&rdquo;</strong> en estado <strong>${postStatus}</strong>.</p>
      ${hasVideo ? '<p class="text-xs text-foreground-400 mt-1">La publicación se vinculará al video generado.</p>' : ''}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Crear publicación',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#9ca3af',
    }).then((result) => {
      if (result.isConfirmed) {
        onAction({
          action: 'post',
          postData: {
            title: postTitle,
            text: postText,
            type: hasVideo ? 'video' : 'imagen',
            status: postStatus,
          },
        });
      }
    });
  };

  const handleArchive = () => {
    sharedSwal.fire({
      title: '¿Archivar live?',
      html: `<p class="text-sm text-foreground-500">El live <strong>&ldquo;${escapeHtml(live.title)}&rdquo;</strong> se archivará. No aparecerá en la lista activa pero conserva todos sus datos.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Archivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6b7280',
      cancelButtonColor: '#9ca3af',
    }).then((result) => {
      if (result.isConfirmed) {
        onAction({ action: 'archive' });
      }
    });
  };

  const handleNotify = () => {
    onAction({
      action: 'notify',
      notifyContext: {
        type: 'transmision',
        relatedModule: 'live',
        relatedItemId: live.id,
        relatedItemName: live.title,
        prefilledTitle: `Repetición disponible: ${live.title}`,
        prefilledMessage: `Ya podés ver la repetición de "${live.title}" con ${live.host}. ¡No te lo pierdas!`,
      },
    });
  };

  const handleBackToMenu = () => setStep('menu');

  // ── Video form step ──
  if (step === 'video_form') {
    return (
      <div className="fixed inset-0 z-[65] flex items-start justify-center p-4 pt-[10vh] overflow-y-auto">
        <div className="absolute inset-0 bg-black/40" onClick={handleBackToMenu}></div>
        <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <div>
              <h3 className="text-base font-bold text-foreground-950">Publicar como Video</h3>
              <p className="text-[11px] text-foreground-400 mt-0.5">El video se creará a partir del live finalizado</p>
            </div>
            <button onClick={handleBackToMenu} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
              <i className="ri-arrow-left-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Thumbnail preview */}
            <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-background-200">
              <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-background-50/90 flex items-center justify-center">
                  <i className="ri-play-fill text-foreground-900 text-2xl ml-0.5"></i>
                </div>
              </div>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="px-2 py-0.5 rounded-md bg-background-50/90 text-foreground-700 text-[9px] font-semibold flex items-center gap-1">
                  <i className={`${providerIcons[live.provider]} text-[10px]`}></i>
                  {providerLabels[live.provider]}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-[10px] font-semibold">
                  {videoDuration}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Título</label>
              <input type="text" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} maxLength={120}
                className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Descripción</label>
              <textarea value={videoDesc} onChange={(e) => setVideoDesc(e.target.value)} maxLength={500} rows={3}
                className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-900 text-sm resize-none focus:outline-none focus:border-accent-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Categoría</label>
              <div className="flex items-center gap-2 flex-wrap">
                {['programas', 'entrevistas', 'eventos', 'shorts', 'promociones'].map((cat) => (
                  <button key={cat} type="button" onClick={() => setVideoCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      videoCategory === cat ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}>
                    {cat === 'programas' ? 'Programas' : cat === 'entrevistas' ? 'Entrevistas' : cat === 'eventos' ? 'Eventos' : cat === 'shorts' ? 'Shorts' : 'Promociones'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Duración</label>
                <input type="text" value={videoDuration} onChange={(e) => setVideoDuration(e.target.value)}
                  placeholder="1:30:00" className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Estado</label>
                <div className="flex items-center gap-1">
                  {(['borrador', 'programado', 'publicado'] as const).map((st) => (
                    <button key={st} type="button" onClick={() => setVideoStatus(st)}
                      className={`flex-1 px-2 py-2 rounded-xl border text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                        videoStatus === st
                          ? st === 'publicado' ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : st === 'programado' ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-foreground-300 bg-foreground-100 text-foreground-700'
                          : 'border-background-200 bg-background-100 text-foreground-400'
                      }`}>
                      {st === 'publicado' ? 'Publicado' : st === 'programado' ? 'Prog.' : 'Borrador'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setVideoFeatured(!videoFeatured)}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 cursor-pointer transition-all ${
                  videoFeatured ? 'bg-amber-500 border-amber-500' : 'bg-transparent border-background-300'
                }`}>
                {videoFeatured && <i className="ri-check-line text-background-50 text-[10px]"></i>}
              </button>
              <div>
                <p className="text-xs font-semibold text-foreground-800">Marcar como destacado</p>
                <p className="text-[10px] text-foreground-400">Aparecerá primero en la sección de videos</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 px-5 py-4 border-t border-background-200 bg-background-50">
            <button onClick={handleBackToMenu} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap">
              Volver
            </button>
            <button onClick={handleConfirmVideo} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap">
              Crear video
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post form step ──
  if (step === 'post_form') {
    return (
      <div className="fixed inset-0 z-[65] flex items-start justify-center p-4 pt-[10vh] overflow-y-auto">
        <div className="absolute inset-0 bg-black/40" onClick={handleBackToMenu}></div>
        <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <div>
              <h3 className="text-base font-bold text-foreground-950">Crear Publicación</h3>
              <p className="text-[11px] text-foreground-400 mt-0.5">Compartí el contenido del live con tu audiencia</p>
            </div>
            <button onClick={handleBackToMenu} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
              <i className="ri-arrow-left-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Preview */}
            <div className="bg-background-100 rounded-xl p-3 flex items-center gap-3">
              <div className="w-16 h-12 rounded-lg overflow-hidden bg-background-200 shrink-0">
                <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground-900 line-clamp-1">Vista previa de la publicación</p>
                <p className="text-[10px] text-foreground-400">{live.host} &bull; {live.date}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Título</label>
              <input type="text" value={postTitle} onChange={(e) => setPostTitle(e.target.value)} maxLength={120}
                className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Texto</label>
              <textarea value={postText} onChange={(e) => setPostText(e.target.value)} maxLength={500} rows={4}
                className="w-full px-3 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-900 text-sm resize-none focus:outline-none focus:border-accent-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Estado de publicación</label>
              <div className="flex items-center gap-1">
                {(['borrador', 'programado', 'publicado'] as const).map((st) => (
                  <button key={st} type="button" onClick={() => setPostStatus(st)}
                    className={`flex-1 px-3 py-2 rounded-xl border text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      postStatus === st
                        ? st === 'publicado' ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : st === 'programado' ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-foreground-300 bg-foreground-100 text-foreground-700'
                        : 'border-background-200 bg-background-100 text-foreground-400'
                    }`}>
                    {st === 'publicado' ? 'Publicado' : st === 'programado' ? 'Programado' : 'Borrador'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 px-5 py-4 border-t border-background-200 bg-background-50">
            <button onClick={handleBackToMenu} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap">
              Volver
            </button>
            <button onClick={handleConfirmPost} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
              Crear publicación
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main menu step ──
  return (
    <div className="fixed inset-0 z-[65] flex items-start justify-center p-4 pt-[10vh] overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-foreground-950">Live finalizado</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-16 h-10 rounded-lg overflow-hidden bg-background-200 shrink-0">
                <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-foreground-950 line-clamp-1">{live.title}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">
                  {live.host} &bull; {new Date(live.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })} &bull; {live.time}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="p-5 space-y-3">
          <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide">¿Qué querés hacer ahora?</p>

          {/* Create recording entry */}
          <button onClick={handleCreateRecording}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all text-left ${
              hasRecording ? 'border-emerald-300 bg-emerald-50/50 opacity-80' : 'border-background-200 bg-background-50 hover:border-emerald-300 hover:bg-emerald-50/30'
            }`}>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-record-circle-line text-emerald-600 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground-900">
                {hasRecording ? 'Grabación ya creada' : 'Crear registro de grabación'}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">
                {hasRecording ? 'Ya existe un registro de grabación para este live.' : 'Genera métricas mock de audiencia y conserva los datos del live.'}
              </p>
            </div>
            {hasRecording ? (
              <i className="ri-check-line text-emerald-600 text-lg"></i>
            ) : (
              <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
            )}
          </button>

          {/* Publish as Video */}
          <button onClick={handlePublishAsVideo}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all text-left ${
              hasVideo ? 'border-amber-300 bg-amber-50/50 opacity-80' : 'border-background-200 bg-background-50 hover:border-amber-300 hover:bg-amber-50/30'
            }`}>
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <i className="ri-video-line text-amber-600 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground-900">
                {hasVideo ? 'Video ya generado' : 'Publicar como Video'}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">
                {hasVideo ? 'Este live ya tiene un video asociado.' : 'Creá un video en /admin/videos con el contenido del live. Elegí categoría, estado y destacado.'}
              </p>
            </div>
            {hasVideo ? (
              <i className="ri-check-line text-amber-600 text-lg"></i>
            ) : (
              <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
            )}
          </button>

          {/* Create Post */}
          <button onClick={handleCreatePost}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all text-left ${
              hasPost ? 'border-sky-300 bg-sky-50/50 opacity-80' : 'border-background-200 bg-background-50 hover:border-sky-300 hover:bg-sky-50/30'
            }`}>
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
              <i className="ri-file-list-3-line text-sky-600 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground-900">
                {hasPost ? 'Publicación ya creada' : 'Crear Publicación'}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">
                {hasPost ? 'Este live ya tiene una publicación asociada.' : 'Compartí el live con tu audiencia. Elegí entre borrador, programado o publicado.'}
              </p>
            </div>
            {hasPost ? (
              <i className="ri-check-line text-sky-600 text-lg"></i>
            ) : (
              <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
            )}
          </button>

          {/* Notify */}
          <button onClick={handleNotify}
            className="w-full flex items-center gap-3 p-4 rounded-xl border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-primary-50/30 cursor-pointer transition-all text-left">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <i className="ri-notification-3-line text-primary-600 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground-900">Notificar repetición</p>
              <p className="text-xs text-foreground-500 mt-0.5">Avisá a tu audiencia que ya pueden ver la repetición del live.</p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
          </button>

          {/* Archive */}
          <button onClick={handleArchive}
            className="w-full flex items-center gap-3 p-4 rounded-xl border border-background-200 bg-background-50 hover:border-foreground-300 hover:bg-foreground-50/30 cursor-pointer transition-all text-left">
            <div className="w-10 h-10 rounded-xl bg-foreground-100 flex items-center justify-center shrink-0">
              <i className="ri-archive-line text-foreground-500 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground-900">Archivar</p>
              <p className="text-xs text-foreground-500 mt-0.5">El live se oculta de la lista activa pero conserva todos sus datos.</p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-background-200 bg-background-100">
          <p className="text-[10px] text-foreground-400 text-center">
            Frontend mock. Las grabaciones, videos y publicaciones se guardan solo durante la sesión.
          </p>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}